// Barkwoo edits to Dawn's file (barkwoo-26tm), marked "Barkwoo edit" below; carry them over
// on a Dawn upgrade. A failed add (a non-JSON or non-OK /cart/add answer, such as Shopify's
// bot-check 429 HTML page, or a network error) used to throw in response.json() and reach a
// catch that only logged it: the spinner stopped and the visitor saw nothing. Now the form's
// error area shows the <product-form data-bw-add-error> text, screen readers hear it from the
// form's [data-bw-atc-live] region, and Dawn's cart error event still fires. The success path
// is unchanged.
if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          // Barkwoo edit (barkwoo-26tm): was `.then((response) => response.json())`.
          .then(
            (response) => this.barkwooReadCartAdd(response),
            (error) => {
              throw this.barkwooAddFailed(error);
            }
          )
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              // Barkwoo edit (barkwoo-26tm): fall back to our text when Shopify sends no description.
              this.handleErrorMessage(response.description || this.barkwooAddErrorText());
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            // Barkwoo edit (barkwoo-26tm): tell the visitor. Only for a failed add, not for an
            // error thrown while drawing the cart after a good one (the item is in the cart).
            if (e && e.barkwooAddFailed) this.handleErrorMessage(this.barkwooAddErrorText());
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }

        // Barkwoo edit (barkwoo-26tm): the visible box is aria-hidden; screen readers hear the
        // text from this always-present live region. Each submit empties it first (the call
        // with no message), so the same error is announced again on a retry.
        this.barkwooLiveRegion = this.barkwooLiveRegion || this.querySelector('[data-bw-atc-live]');
        if (this.barkwooLiveRegion) this.barkwooLiveRegion.textContent = errorMessage || '';
      }

      // Barkwoo edit (barkwoo-26tm): Dawn's response.json(), made safe. A JSON answer with a
      // `status` (422 sold out and other cart errors) still takes Dawn's own path; an empty or
      // non-JSON answer, or a non-OK one without that status, becomes a failed add.
      barkwooReadCartAdd(response) {
        return response.json().then(
          (body) => {
            if (!body || (!response.ok && !body.status)) {
              throw this.barkwooAddFailed(new Error(`Cart add failed: HTTP ${response.status}`));
            }
            return body;
          },
          () => {
            throw this.barkwooAddFailed(new Error(`Cart add failed: HTTP ${response.status}, not JSON`));
          }
        );
      }

      // Barkwoo edit (barkwoo-26tm): marks an error as a failed add for the catch.
      barkwooAddFailed(error) {
        const failure = error instanceof Error ? error : new Error(String(error));
        failure.barkwooAddFailed = true;
        return failure;
      }

      // Barkwoo edit (barkwoo-26tm): the locale text from data-bw-add-error (snippets/buy-buttons,
      // bw-home-atc), else Dawn's cart error string.
      barkwooAddErrorText() {
        return this.dataset.bwAddError || window.cartStrings?.error;
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
